/**
 * Chummys baking soon — <chummys-xmas>
 *
 * Three small jobs for the seasonal band:
 *   1. the countdown to the launch, computed in the shop's timezone so a
 *      visitor abroad sees the same "doors open in" as one in Droitwich;
 *      when it reaches zero the band flips to its live state;
 *   2. the waiting-list form's success state — Shopify bounces back to the
 *      page with ?customer_posted=true and the form's id as the hash;
 *   3. the snow: a canvas of soft flakes over the band, paused off-screen,
 *      skipped entirely under prefers-reduced-motion.
 */
(() => {
  if (customElements.get('chummys-xmas')) return;

  const mqReduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  /** epoch ms of "YYYY-MM-DD HH:MM" read as wall-clock time in `tz` */
  function zonedEpoch(str, tz) {
    const m = String(str || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/);
    if (!m) return NaN;
    const naive = Date.UTC(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0));
    try {
      const parts = new Intl.DateTimeFormat('en-GB', { timeZone: tz, timeZoneName: 'longOffset' }).formatToParts(new Date(naive));
      const tzn = (parts.find(p => p.type === 'timeZoneName') || {}).value || 'GMT';
      const om = tzn.match(/([+-])(\d{2}):?(\d{2})?/);
      const offset = om ? (om[1] === '-' ? -1 : 1) * ((+om[2]) * 60 + (+(om[3] || 0))) : 0;
      return naive - offset * 60000;
    } catch (e) { return naive; }
  }

  class ChummysXmas extends HTMLElement {
    connectedCallback() {
      this.units = {
        d: this.querySelector('[data-days]'), h: this.querySelector('[data-hours]'),
        m: this.querySelector('[data-mins]'), s: this.querySelector('[data-secs]'),
      };
      this.target = zonedEpoch(this.dataset.launch, this.dataset.tz || 'Europe/London');
      this.canvas = this.querySelector('.chummys-xmas__snow');

      // 1. countdown
      if (!isNaN(this.target)) {
        this.tick();
        this.timer = setInterval(() => this.tick(), 1000);
      }

      // 2. the list's thank-you
      this.successState();

      // 3. arrival + snow, both keyed off visibility
      this.io = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (e.isIntersecting) { this.classList.add('is-in'); this.snowOn(); }
          else this.snowOff();
        }
      }, { threshold: 0.12 });
      this.io.observe(this);
    }

    disconnectedCallback() {
      if (this.timer) clearInterval(this.timer);
      if (this.io) this.io.disconnect();
      this.snowOff();
    }

    /* ------------------------------------------------------------- 1 */
    tick() {
      const left = this.target - Date.now();
      if (left <= 0) {
        this.classList.add('is-live');
        if (this.timer) clearInterval(this.timer);
        return;
      }
      const s = Math.floor(left / 1000);
      const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
      const set = (el, v) => { if (el) { const t = String(v).padStart(2, '0'); if (el.textContent !== t) el.textContent = t; } };
      set(this.units.d, d); set(this.units.h, h); set(this.units.m, m); set(this.units.s, sec);
    }

    /* ------------------------------------------------------------- 2 */
    successState() {
      const list = this.querySelector('.chummys-xmas__list');
      const form = this.querySelector('form');
      if (!list || !form) return;
      let posted = false;
      try { posted = new URLSearchParams(location.search).get('customer_posted') === 'true' && location.hash === '#' + form.id; } catch (e) {}
      if (!posted) return;
      list.classList.add('is-done');
      const ok = list.querySelector('.chummys-xmas__success');
      if (ok) ok.hidden = false;
      // land on the thank-you rather than wherever the browser left the page
      requestAnimationFrame(() => this.scrollIntoView({ block: 'center' }));
    }

    /* ------------------------------------------------------------- 3 */
    snowOn() {
      if (!this.canvas || this.dataset.snow === 'false' || mqReduced.matches || this.snowing) return;
      this.snowing = true;
      const ctx = this.canvas.getContext('2d');
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      let w = 0, h = 0, flakes = [];

      const size = () => {
        const r = this.getBoundingClientRect();
        w = Math.max(1, Math.round(r.width)); h = Math.max(1, Math.round(r.height));
        this.canvas.width = Math.round(w * dpr); this.canvas.height = Math.round(h * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        const n = Math.round(Math.min(110, Math.max(40, (w * h) / 9000)));
        flakes = Array.from({ length: n }, () => this.flake(w, h, true));
      };
      this.ro = new ResizeObserver(size); this.ro.observe(this);
      size();

      let last = performance.now();
      const draw = (now) => {
        if (!this.snowing) return;
        const dt = Math.min(0.05, (now - last) / 1000); last = now;
        ctx.clearRect(0, 0, w, h);
        for (const f of flakes) {
          f.t += dt;
          f.y += f.vy * dt;
          f.x += (f.vx + Math.sin(f.t * f.sway) * 12) * dt;
          if (f.y - f.r > h) Object.assign(f, this.flake(w, h, false));
          if (f.x < -10) f.x = w + 10; else if (f.x > w + 10) f.x = -10;
          ctx.globalAlpha = f.a;
          ctx.beginPath(); ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2);
          ctx.fillStyle = '#fff'; ctx.fill();
        }
        ctx.globalAlpha = 1;
        this.raf = requestAnimationFrame(draw);
      };
      this.raf = requestAnimationFrame(draw);
    }

    flake(w, h, anywhere) {
      const depth = Math.random();                       // 0 far, 1 near
      return {
        x: Math.random() * w, y: anywhere ? Math.random() * h : -8,
        r: 0.8 + depth * 2.6, a: 0.25 + depth * 0.6,
        vy: 14 + depth * 34, vx: -4 + Math.random() * 8,
        sway: 0.6 + Math.random() * 1.2, t: Math.random() * 10,
      };
    }

    snowOff() {
      this.snowing = false;
      if (this.raf) cancelAnimationFrame(this.raf);
      if (this.ro) this.ro.disconnect();
      this.raf = null; this.ro = null;
    }
  }

  customElements.define('chummys-xmas', ChummysXmas);
})();
